import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { HttpError } from 'react-admin';
import { getSessionClaims, isDemoSession } from '../apiClient.ts';
import { RecordSection } from '../components/record/RecordPage.tsx';
import { getErrorMessage } from '../utils/helperUtils.ts';
import TeamMembers from './TeamMembers.tsx';
import { fetchTeam, type TeamMember, workspaceTeam } from './teamApi.ts';

/**
 * Settings > Team, for the workspace's admins. It asks the API rather than the token: a role
 * changed since sign-in shows the right thing, and anyone else never sees the section.
 */
const TeamSection = () => {
  const demo = isDemoSession();
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [hidden, setHidden] = useState(demo);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMembers(await fetchTeam());
      setError(null);
    } catch (err) {
      // 400: not an admin here, so there is nothing to show.
      if (err instanceof HttpError && err.status === 400) {
        setHidden(true);
      } else {
        setError(getErrorMessage(err));
      }
    }
  }, []);

  useEffect(() => {
    if (!demo) void load();
  }, [demo, load]);

  if (hidden) return null;

  return (
    <RecordSection
      title="Team"
      description="Everyone who can sign in to this workspace. Revoking access signs someone out on their next click."
      sx={{ mt: 2 }}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not load the team: {error}
        </Alert>
      ) : null}
      {members ? (
        <TeamMembers
          members={members}
          actions={workspaceTeam}
          selfEmail={getSessionClaims()?.email}
          onChange={() => void load()}
        />
      ) : error ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading the team
          </Typography>
        </Box>
      )}
    </RecordSection>
  );
};

export default TeamSection;
